/**
 * Servicio de configuración centralizada
 * Basado en JsonRequest.cs
 */

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

export interface Config {
  client: string;
  assistantID: string;
  testingPhase: boolean;
  testingWebSocket: boolean;
  useApolearn: boolean; // Si es true, requiere token de auth; si es false, no requiere
  accessToken?: string;
  refreshToken?: string;
  email?: string;
  userFullName?: string;
  userFirstname?: string;
  userLastname?: string;
  userID?: number;
  promptReference?: string;
  fullPrompt?: string;
  practiceMode?: string; // Modo de práctica (conversacional, estudio, examen, etc.)
  accent?: string; // Acento seleccionado (Francia, Canadá, Australia, Bélgica)
  // Nuevos parámetros de personalización
  personalityType?: string; // Personalidad de Galia (Amigable, Sarcástica, etc.)
  functionalityType?: string; // Funcionalidad de Galia (Profe, Conversadora)
  mcerLevel?: string; // Nivel MCER del estudiante (A1, A2, B1, B2, C1, C2)
}

class ConfigService {
  private config: Config = {
    client: 'alianzafrancesa',
    assistantID: '',
    testingPhase: true,
    testingWebSocket: true,
    useApolearn: false, // Por defecto: no apolearn (no requiere auth)
  };

  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      timeout: 30000,
    });

    // Interceptor para agregar token Bearer
    this.axiosInstance.interceptors.request.use(
      (config) => {
        if (this.config.accessToken) {
          config.headers.Authorization = `Bearer ${this.config.accessToken.trim()}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor pass-through de respuestas
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => Promise.reject(error)
    );
  }

  initialize(config: Partial<Config>): void {
    this.config = { ...this.config, ...config };
  }

  getConfig(): Config {
    return { ...this.config };
  }

  setAccessToken(token: string): void {
    this.config.accessToken = token;
  }

  setRefreshToken(token: string): void {
    this.config.refreshToken = token;
  }

  /**
   * Guarda información del usuario sin sobrescribir valores previos con undefined.
   * Los llamadores pueden pasar campos parciales (p. ej. solo email + fullName desde LTI,
   * y más tarde user_id + firstname/lastname cuando getApolearnCompleteData() resuelve).
   */
  setUserInfo(email: string, fullName: string, userID?: number, firstname?: string, lastname?: string): void {
    if (email) this.config.email = email;
    if (fullName) this.config.userFullName = fullName;
    if (firstname !== undefined) this.config.userFirstname = firstname;
    if (lastname !== undefined) this.config.userLastname = lastname;
    if (userID && userID > 0) this.config.userID = userID;
  }

  /**
   * Actualizar promptReference dinámicamente
   */
  setPromptReference(promptReference: string): void {
    const previousReference = this.config.promptReference;
    if (previousReference === promptReference) {
      return;
    }
    this.config.promptReference = promptReference;
    // Limpiar el prompt completo para forzar recarga con nuevo reference
    this.config.fullPrompt = undefined;
  }

  /**
   * Actualizar avatarType/mode para el avatar
   */
  setAvatarType(avatarType: string): void {
    // Guardar en una propiedad adicional si es necesario
    (this.config as any).avatarType = avatarType;
  }

  getAvatarType(): string | undefined {
    return (this.config as any).avatarType;
  }

  /**
   * Guardar modo de práctica y acento para configuración de voz
   */
  setPracticeModeAndAccent(mode: string, accent: string): void {
    const changed = this.config.practiceMode !== mode || this.config.accent !== accent;
    this.config.practiceMode = mode;
    this.config.accent = accent;
    // El prompt ensamblado depende del acento; invalidar cache para forzar regeneracion.
    if (changed) {
      this.config.fullPrompt = undefined;
    }
  }

  getPracticeMode(): string | undefined {
    return this.config.practiceMode;
  }

  getAccent(): string | undefined {
    return this.config.accent;
  }

  /**
   * Guardar personalidad de Galia
   */
  setPersonalityType(personalityType: string): void {
    if (this.config.personalityType !== personalityType) {
      this.config.fullPrompt = undefined; // invalidar prompt cacheado
    }
    this.config.personalityType = personalityType;
    this.savePersonalizationToCache();
  }

  getPersonalityType(): string | undefined {
    return this.config.personalityType;
  }

  /**
   * Guardar funcionalidad de Galia
   */
  setFunctionalityType(functionalityType: string): void {
    if (this.config.functionalityType !== functionalityType) {
      this.config.fullPrompt = undefined; // invalidar prompt cacheado
    }
    this.config.functionalityType = functionalityType;
    this.savePersonalizationToCache();
  }

  getFunctionalityType(): string | undefined {
    return this.config.functionalityType;
  }

  /**
   * Guardar nivel MCER del estudiante
   */
  setMCERLevel(mcerLevel: string): void {
    if (this.config.mcerLevel !== mcerLevel) {
      this.config.fullPrompt = undefined; // invalidar prompt cacheado
    }
    this.config.mcerLevel = mcerLevel;
    this.savePersonalizationToCache();
  }

  getMCERLevel(): string | undefined {
    return this.config.mcerLevel;
  }

  /**
   * Guardar toda la configuración de personalización en cache (localStorage)
   */
  private savePersonalizationToCache(): void {
    try {
      const personalization = {
        personalityType: this.config.personalityType,
        functionalityType: this.config.functionalityType,
        mcerLevel: this.config.mcerLevel,
      };
      localStorage.setItem('galia_personalization', JSON.stringify(personalization));
    } catch {
      // Error silencioso al guardar personalización en caché
    }
  }

  /**
   * Cargar configuración de personalización desde cache (localStorage)
   */
  /** Invalida el prompt ensamblado en caché: se reensambla al (re)conectar. */
  invalidateFullPrompt(): void {
    this.config.fullPrompt = undefined;
  }

  loadPersonalizationFromCache(): void {
    try {
      const cached = localStorage.getItem('galia_personalization');
      if (cached) {
        const personalization = JSON.parse(cached);
        // Igual que los setters: si algo cambia, el prompt en caché ya no vale.
        if (
          this.config.personalityType !== personalization.personalityType ||
          this.config.functionalityType !== personalization.functionalityType ||
          this.config.mcerLevel !== personalization.mcerLevel
        ) {
          this.config.fullPrompt = undefined;
        }
        this.config.personalityType = personalization.personalityType;
        this.config.functionalityType = personalization.functionalityType;
        this.config.mcerLevel = personalization.mcerLevel;
      }
    } catch {
      // Error silencioso al cargar personalización desde caché
    }
  }

  // Métodos HTTP helpers
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, {
      ...config,
      headers: {
        'Content-Type': 'application/json',
        ...config?.headers,
      },
    });
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.patch<T>(url, data, {
      ...config,
      headers: {
        'Content-Type': 'application/json',
        ...config?.headers,
      },
    });
    return response.data;
  }

  async getWithBearerToken<T>(url: string, token: string): Promise<T> {
    const response = await axios.get<T>(url, {
      headers: {
        Authorization: `Bearer ${token.trim()}`,
      },
    });
    return response.data;
  }

  async postWithBearerToken<T>(url: string, token: string, data: any): Promise<T> {
    const response = await axios.post<T>(url, data, {
      headers: {
        Authorization: `Bearer ${token.trim()}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  async patchWithBearerToken<T>(url: string, token: string, data: any): Promise<T> {
    const response = await axios.patch<T>(url, data, {
      headers: {
        Authorization: `Bearer ${token.trim()}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  /**
   * Actualizar el prompt completo después de obtenerlo
   */
  setFullPrompt(fullPrompt: string): void {
    this.config.fullPrompt = fullPrompt;
  }
}

export const configService = new ConfigService();
export default configService;


